import { concat, fn, get, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { importSync } from '@embroider/macros';

import can from 'ember-can/helpers/can';
import cannot from 'ember-can/helpers/cannot';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, not } from 'ember-truth-helpers';
import { trackedFunction } from 'reactiveweb/function';

import Button from 'umg-ember-backend/components/ui/button/index';
import Modal from 'umg-ember-backend/components/ui/modal/index';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import Component from '../belongs-to';
import styles from './styles.module.css';

const bem = getBem(styles);

export default class UiFormFieldMasManyComponent extends Component {
  // Getter and setter
  get RecordExplorerForAssign() {
    const type = this.relationType ?? 'article';

    const module = importSync(`../../../../record-explorer/for/${type}`);

    return module.default;
  }

  get sortedRecords() {
    return this.sortRecords.value ?? [];
  }

  get sortable() {
    return this.args.sortable ?? true;
  }

  get sortableColumn() {
    return this.args.sortableColumn ?? 'position';
  }

  get sortableNotDisabled() {
    return this.sortable && !this.args.isDisabled;
  }

  get sortableGroup() {
    return guidFor(this);
  }

  // Funcions
  setRelation = (records) => {
    let { model, property } = this.args;

    this.recordRelationships.watch(model, property, async () => {
      let existingRecords = await model[property];

      if (this.args.insertAtEnd) {
        model[property] = [...existingRecords, ...records];
      } else {
        model[property] = [...records, ...existingRecords];
      }
    });
  };

  reorderItems = (items) => {
    let { model, reorderItems, property } = this.args;
    if (reorderItems) {
      reorderItems(items);
    } else {
      items.forEach((item, index) => {
        item.set(this.sortableColumn, index);
      });

      model.set(property, items);
    }
  };

  removeRecord = (record) => {
    let { model, property } = this.args;

    this.recordRelationships.watch(model, property, async () => {
      let existingRecords = await model[property];
      let indexOfRecord = existingRecords.indexOf(record);

      existingRecords.splice(indexOfRecord, 1);
    });
  };

  sortRecords = trackedFunction(this, async () => {
    let records = await this.args.value;

    return records.slice().sort((a, b) => {
      return a[this.sortableColumn] > b[this.sortableColumn];
    });
  });

  // Template
  <template>
    <div
      class={{bem (hash is-sortable=this.sortableNotDisabled)}}
      id={{@fieldId}}
      ...attributes
    >
      {{#if this.sortedRecords}}
        <ol
          class={{bem 'records'}}
          {{sortableGroup
            disabled=(not this.sortableNotDisabled)
            groupName=this.sortableGroup
            onChange=this.reorderItems
          }}
        >
          {{#each this.sortedRecords as |record index|}}
            <li
              class={{bem 'record'}}
              data-id={{record.id}}
              {{sortableItem groupName=this.sortableGroup model=record}}
            >
              <div
                class={{bem
                  'wrapper'
                  (hash
                    has-errors=(and @showErrors record.validations.isInvalid)
                  )
                }}
              >
                <div class={{bem 'handle'}} {{sortableHandle}}>
                  {{svgJar 'drag' class=(bem 'drag-indicator')}}
                </div>
                <div class={{bem 'value'}}>
                  {{#if (has-block)}}
                    {{yield record this.labelProperty index}}
                  {{else}}
                    {{get record this.labelProperty}}
                  {{/if}}
                </div>
              </div>
              <div class={{bem 'actions'}}>
                {{#if this.on.openRecord}}
                  <Button
                    @style='outline'
                    @disabled={{cannot 'read' record parentModel=@model}}
                    @icon='edit'
                    data-select-ui-button
                    {{on 'click' (fn this.on.openRecord record)}}
                  />
                {{/if}}
                {{#if (and this.on.removeRecord (can 'update' @recordType))}}
                  <Button
                    @style='outline'
                    @disabled={{@isDisabled}}
                    @icon={{if @on.removeRecord 'delete' 'close'}}
                    @iconClass='u-color-error'
                    data-select-ui-button
                    {{on
                      'click'
                      (fn
                        this.on.removeRecord
                        record
                        @model
                        this.relationKind
                        @property
                      )
                    }}
                  />
                {{/if}}
              </div>
            </li>
          {{/each}}
        </ol>
      {{/if}}
      {{#if (can 'update' @recordType)}}
        <div class={{bem 'add-new'}}>
          {{#if this.on.openRecordList}}
            <Button
              @style='outline'
              @disabled={{@isDisabled}}
              @icon='search'
              {{on
                'click'
                (fn this.on.openRecordList this.relationType @property)
              }}
            />
          {{/if}}
          {{#if
            (and
              this.on.openNewRecord
              (can 'create' this.relationType parentModel=@model)
            )
          }}
            <Button
              @style='outline'
              @disabled={{@isDisabled}}
              @icon='add'
              @label={{t 'ui.form.field.has_many.new'}}
              data-test-button={{concat 'add-' this.relationType}}
              {{on
                'click'
                (fn
                  this.on.openNewRecord
                  @model
                  this.relationKind
                  this.relationType
                  @property
                  @filters
                )
              }}
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
    <Modal
      @onClose={{this.closeRecordList}}
      @dataDependencies={{this.isRecordListOpen}}
    >
      <this.RecordExplorerForAssign
        @filters={{@filters}}
        @onAssignRelation={{this.on.assignRecords}}
        @recordType={{this.relationType}}
        @options={{@options}}
        @parentRecordType={{getRecordType @model}}
        @relationType={{this.relationKind}}
        @scrollPosition={{this.scrollPositionAssign}}
        @onBodyScroll={{fn (mut this.scrollPositionAssign)}}
      />
    </Modal>
  </template>
}
